import { useState } from 'react'
import { Send, MessageSquare } from 'lucide-react'

/**
 * NoteRapide — Sezione note interne su un obiettivo
 *
 * Lista note con autore/data + campo input per nuova nota.
 * Solo coordinatore. Non appare in report/timeline/PDF.
 *
 * Props:
 *   noteInterne  - Array di note esistenti [{testo, autoreNome, createdAt}]
 *   pepId        - ID del PEP
 *   obiettivoId  - ID dell'obiettivo
 *   onAddNota    - callback(testo) — chiama API
 */

export default function NoteRapide({ noteInterne = [], pepId, obiettivoId, onAddNota }) {
  const [testo, setTesto] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    const trimmed = testo.trim()
    if (!trimmed || submitting) return

    setSubmitting(true)
    try {
      await onAddNota(trimmed)
      setTesto('')
    } catch {
      // errore gestito dal parent
    } finally {
      setSubmitting(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="h-3.5 w-3.5 text-gray-400" />
        <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400">
          Note interne
        </h5>
        <span className="text-[10px] text-gray-400">
          (solo coordinatore)
        </span>
      </div>

      {/* Lista note esistenti */}
      {noteInterne.length > 0 && (
        <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
          {[...noteInterne].reverse().map((nota, idx) => (
            <div key={nota._id || idx} className="flex gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 mt-1.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-gray-700 dark:text-gray-300">{nota.testo}</p>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-400">
                  {nota.autoreNome && <span>{nota.autoreNome}</span>}
                  {nota.createdAt && (
                    <span>
                      {new Date(nota.createdAt).toLocaleDateString('it-IT', {
                        day: 'numeric', month: 'short'
                      })}
                      {' '}
                      {new Date(nota.createdAt).toLocaleTimeString('it-IT', {
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Input nuova nota */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={testo}
          onChange={(e) => setTesto(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Aggiungi nota rapida..."
          className="form-input flex-1 text-sm py-1.5"
          maxLength={500}
          disabled={submitting}
        />
        <button
          onClick={handleSubmit}
          disabled={!testo.trim() || submitting}
          className="p-1.5 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Invia nota"
        >
          {submitting ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  )
}
